/**
 * Hook useDbPerformance - Gerenciamento de dados de performance do banco
 * 
 * Fornece acesso às métricas de performance, índices, bloat e conexões
 * através das RPCs do Supabase. Atualização automática a cada 60 segundos.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { Json } from '@/integrations/supabase/types';

// =====================================================
// TIPOS
// =====================================================

/** Dados de performance por tabela */
export interface PerformanceReport {
  table_name: string;
  seq_scans: number;
  idx_scans: number;
  rows_estimate: number;
  last_analyze: string | null;
  last_autoanalyze: string | null;
  cache_hit_ratio: string | number;
  index_usage_ratio: string | number;
}

/** Dados de índices */
export interface IndexReport {
  table_name: string;
  index_name: string;
  index_size: string;
  idx_scan: number;
  idx_tup_read: number;
  idx_tup_fetch: number;
  index_type?: string;
  is_unique?: boolean;
}

/** Dados de bloat (tuplas mortas) */
export interface BloatReport {
  table_name: string;
  dead_tuples: number;
  live_tuples: number;
  dead_ratio: number;
  last_vacuum: string | null;
  last_autovacuum: string | null;
  needs_vacuum: boolean;
}

/** Estatísticas de conexões */
export interface ConnectionStats {
  total_connections: number;
  active_connections: number;
  idle_connections: number;
  idle_in_transaction: number;
  max_connections: number;
  connection_usage_percent: number;
}

/** Snapshot de performance */
export interface PerformanceSnapshot {
  id: string;
  created_at: string;
  snapshot_type: string;
  notes: string | null;
  snapshot_data: Json;
}

// =====================================================
// FUNÇÕES DE FETCH
// =====================================================

/**
 * Busca relatório de performance das tabelas
 */
async function fetchPerformanceReport(): Promise<PerformanceReport[]> {
  const { data, error } = await supabase.rpc('get_performance_report');
  
  if (error) {
    logger.error('[useDbPerformance] Erro ao buscar performance report:', error.message);
    throw error;
  }
  
  return (data || []) as PerformanceReport[];
}

/**
 * Busca relatório de índices
 */
async function fetchIndexReport(): Promise<IndexReport[]> {
  const { data, error } = await supabase.rpc('get_index_report');
  
  if (error) {
    logger.error('[useDbPerformance] Erro ao buscar index report:', error.message);
    throw error;
  }
  
  return (data || []) as IndexReport[];
}

/**
 * Busca relatório de bloat (tuplas mortas)
 */
async function fetchBloatReport(): Promise<BloatReport[]> {
  const { data, error } = await supabase.rpc('get_table_bloat_report');
  
  if (error) {
    logger.error('[useDbPerformance] Erro ao buscar bloat report:', error.message);
    throw error;
  }
  
  return (data || []) as BloatReport[];
}

/**
 * Busca estatísticas de conexões
 */
async function fetchConnectionStats(): Promise<ConnectionStats | null> {
  const { data, error } = await supabase.rpc('get_connection_stats');
  
  if (error) {
    logger.error('[useDbPerformance] Erro ao buscar connection stats:', error.message);
    throw error;
  }
  
  return data?.[0] as ConnectionStats || null;
}

/**
 * Busca snapshots históricos
 */
async function fetchSnapshots(limit: number = 10): Promise<PerformanceSnapshot[]> {
  const { data, error } = await supabase.rpc('get_performance_snapshots', { p_limit: limit });
  
  if (error) {
    logger.error('[useDbPerformance] Erro ao buscar snapshots:', error.message);
    throw error;
  }
  
  return (data || []) as PerformanceSnapshot[];
}

/**
 * Salva um novo snapshot
 */
async function saveSnapshot(notes?: string): Promise<string> {
  const { data, error } = await supabase.rpc('save_performance_snapshot', {
    p_snapshot_type: 'manual',
    p_notes: notes || null
  });
  
  if (error) {
    logger.error('[useDbPerformance] Erro ao salvar snapshot:', error.message);
    throw error;
  }
  
  return data;
}

// =====================================================
// HOOK PRINCIPAL
// =====================================================

/**
 * Hook para gerenciar dados de performance do banco de dados
 * 
 * @param options.refetchInterval - Intervalo de atualização em ms (padrão: 60000)
 * @param options.enabled - Se as queries devem ser executadas (padrão: true)
 */
export function useDbPerformance(options?: {
  refetchInterval?: number;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const refetchInterval = options?.refetchInterval ?? 60000; // 60 segundos
  const enabled = options?.enabled ?? true;

  // Query: Performance Report
  const performanceQuery = useQuery({
    queryKey: ['db-performance-report'],
    queryFn: fetchPerformanceReport,
    refetchInterval,
    enabled,
    staleTime: 30000,
  });

  // Query: Index Report
  const indexQuery = useQuery({
    queryKey: ['db-index-report'],
    queryFn: fetchIndexReport,
    refetchInterval,
    enabled,
    staleTime: 30000,
  });

  // Query: Bloat Report
  const bloatQuery = useQuery({
    queryKey: ['db-bloat-report'],
    queryFn: fetchBloatReport,
    refetchInterval,
    enabled,
    staleTime: 30000,
  });

  // Query: Connection Stats
  const connectionQuery = useQuery({
    queryKey: ['db-connection-stats'],
    queryFn: fetchConnectionStats,
    refetchInterval,
    enabled,
    staleTime: 30000,
  });

  // Query: Snapshots (sem auto-refresh)
  const snapshotsQuery = useQuery({
    queryKey: ['db-performance-snapshots'],
    queryFn: () => fetchSnapshots(10),
    enabled,
    staleTime: 60000,
  });

  // Mutation: Salvar Snapshot
  const saveSnapshotMutation = useMutation({
    mutationFn: saveSnapshot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['db-performance-snapshots'] });
    },
  });

  // Função para atualizar todas as queries
  const refetchAll = () => {
    performanceQuery.refetch();
    indexQuery.refetch();
    bloatQuery.refetch();
    connectionQuery.refetch();
    snapshotsQuery.refetch();
  };

  // Helper para converter valor para número
  const toNumber = (val: string | number | undefined): number => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val) || 0;
    return 0;
  };

  // Métricas agregadas para KPIs
  const kpis = {
    avgCacheHitRatio: performanceQuery.data
      ? (performanceQuery.data.reduce((sum, t) => sum + toNumber(t.cache_hit_ratio), 0) / 
         performanceQuery.data.length).toFixed(2)
      : '0',
    avgIndexUsageRatio: performanceQuery.data
      ? (performanceQuery.data.reduce((sum, t) => sum + toNumber(t.index_usage_ratio), 0) / 
         performanceQuery.data.length).toFixed(2)
      : '0',
    tablesNeedingVacuum: bloatQuery.data?.filter(t => t.needs_vacuum).length || 0,
    connectionUsage: connectionQuery.data?.connection_usage_percent || 0,
    criticalTables: performanceQuery.data?.filter(t => toNumber(t.index_usage_ratio) < 50).length || 0,
  };

  return {
    // Dados
    performance: performanceQuery.data || [],
    indexes: indexQuery.data || [],
    bloat: bloatQuery.data || [],
    connections: connectionQuery.data,
    snapshots: snapshotsQuery.data || [],
    kpis,

    // Estados de loading
    isLoading: performanceQuery.isLoading || indexQuery.isLoading || 
               bloatQuery.isLoading || connectionQuery.isLoading,
    isLoadingPerformance: performanceQuery.isLoading,
    isLoadingIndexes: indexQuery.isLoading,
    isLoadingBloat: bloatQuery.isLoading,
    isLoadingConnections: connectionQuery.isLoading,
    isLoadingSnapshots: snapshotsQuery.isLoading,

    // Estados de erro
    error: performanceQuery.error || indexQuery.error || 
           bloatQuery.error || connectionQuery.error,
    
    // Ações
    refetchAll,
    saveSnapshot: saveSnapshotMutation.mutate,
    isSavingSnapshot: saveSnapshotMutation.isPending,
  };
}
